import { ActivatedRouteSnapshot, Router } from "@angular/router";

const EXTERNAL_RETURN_URL_PATTERN = /^https?:\/\//i;

/** Query param that marks where the user entered a public app page from. */
export const NAV_RETURN_FROM_QUERY_PARAM = "from";

/** Landing / marketing home entry — label only; URL comes from `returnUrl` qp. */
export const NAV_RETURN_FROM_HOME = "home";

type NavReturnLabelScope = "knowledge_base" | "legal";

const NAV_RETURN_LABEL_BY_FROM: Record<
  string,
  { kbLabelKey: string; legalLabelKey: string }
> = {
  [NAV_RETURN_FROM_HOME]: {
    kbLabelKey: "knowledge_base.return.landing",
    legalLabelKey: "legal.return.landing",
  },
};

export function readNavReturnFromParam(
  route: Pick<ActivatedRouteSnapshot, "queryParamMap">,
): string | null {
  return route.queryParamMap.get(NAV_RETURN_FROM_QUERY_PARAM)?.trim() || null;
}

export function resolveNavReturnLabelFromQuery(
  from: string | null | undefined,
  scope: NavReturnLabelScope,
): string | null {
  if (!from) {
    return null;
  }

  const config = NAV_RETURN_LABEL_BY_FROM[from];
  if (!config) {
    return null;
  }

  return scope === "legal" ? config.legalLabelKey : config.kbLabelKey;
}

/** True when the user is in the landing/public marketing flow. */
export function isNavReturnFromHome(options: {
  from?: string | null;
  returnUrl?: string | null;
}): boolean {
  if (options.from === NAV_RETURN_FROM_HOME) {
    return true;
  }

  const returnUrl = options.returnUrl ?? "";
  if (
    returnUrl.includes(
      `${NAV_RETURN_FROM_QUERY_PARAM}=${NAV_RETURN_FROM_HOME}`,
    )
  ) {
    return true;
  }

  if (EXTERNAL_RETURN_URL_PATTERN.test(returnUrl)) {
    try {
      const pathname = new URL(returnUrl).pathname;
      return pathname === "/" || pathname.startsWith("/landing");
    } catch {
      return false;
    }
  }

  const path = returnUrl.split("?")[0].split("#")[0];
  return path === "/" || path.startsWith("/landing");
}

export function copyNavReturnQueryParams(
  queryParams: Record<string, string | null | undefined>,
): Record<string, string> {
  const result: Record<string, string> = {};
  const from = queryParams[NAV_RETURN_FROM_QUERY_PARAM]?.trim();
  const returnUrl = queryParams["returnUrl"]?.trim();
  const returnLabelKey = queryParams["returnLabelKey"]?.trim();
  const backRedirect = queryParams["backRedirect"]?.trim();

  if (from) {
    result[NAV_RETURN_FROM_QUERY_PARAM] = from;
  }

  if (returnUrl) {
    result["returnUrl"] = returnUrl;
  } else if (backRedirect) {
    result["backRedirect"] = backRedirect;
  }

  if (returnLabelKey) {
    result["returnLabelKey"] = returnLabelKey;
  }

  return result;
}

export function appendNavReturnQueryParams(
  params: URLSearchParams,
  queryParams: Record<string, string | null | undefined>,
): void {
  for (const [key, value] of Object.entries(
    copyNavReturnQueryParams(queryParams),
  )) {
    params.set(key, value);
  }
}

/** In-app route or full marketing URL (e.g. landing from `returnUrl` qp). */
export function navigateToReturnUrl(
  router: Router,
  returnUrl: string | null | undefined,
  fallback = "/dashboard",
): void {
  const target = returnUrl?.trim() || fallback;

  if (EXTERNAL_RETURN_URL_PATTERN.test(target)) {
    window.location.assign(target);
    return;
  }

  void router.navigateByUrl(target);
}
